import React, { useEffect, useRef, useCallback } from "react";
import { uploadDocumentFiles } from "./document-upload-service";

export const UploaderV4 = ({
  onUploadStart,
  filesToUpload,
  onUploadComplete,
  onProgressChange,
  onFileUploadComplete,
  onReset,
}) => {
  const uploadFunctionRef = useRef(null);
  const hasNotifiedRef = useRef(false);
  const previousFilesLengthRef = useRef(0);

  const startUpload = useCallback(async () => {
    if (!filesToUpload || filesToUpload.length === 0) {
      return;
    }

    const uploadedFiles = await uploadDocumentFiles(filesToUpload, {
      onProgressChange,
      onFileUploadComplete,
    });

    if (onUploadComplete) {
      onUploadComplete(uploadedFiles);
    }
  }, [filesToUpload, onProgressChange, onFileUploadComplete, onUploadComplete]);

  // Always keep ref in sync (e.g. user edits titles — same file count, new `startUpload` closure).
  useEffect(() => {
    uploadFunctionRef.current = startUpload;
  }, [startUpload]);

  // Notify parent when staged files appear (only when length > 0).
  useEffect(() => {
    const currentFilesLength = filesToUpload?.length || 0;

    if (currentFilesLength === 0) {
      hasNotifiedRef.current = false;
      previousFilesLengthRef.current = 0;
      return;
    }

    const filesChanged = currentFilesLength !== previousFilesLengthRef.current;

    if (onUploadStart && (filesChanged || !hasNotifiedRef.current)) {
      hasNotifiedRef.current = true;
      previousFilesLengthRef.current = currentFilesLength;

      onUploadStart(() => {
        return uploadFunctionRef.current();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesToUpload?.length, onUploadStart]);

  // Reset function
  const resetUploadProgress = useCallback(() => {
    hasNotifiedRef.current = false;
    previousFilesLengthRef.current = 0;
  }, []);

  // Provide reset function to parent
  useEffect(() => {
    if (onReset) {
      onReset(resetUploadProgress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReset]);

  return null;
};
